import React, { useState, useEffect, useCallback } from 'react';
import { Search, CalendarRange, X, ChevronDown, ChevronUp, Fingerprint, CalendarOff, RefreshCw, Clock, Plus, Pencil, Trash2 } from 'lucide-react';
import { apiFetch } from '../utils/apiClient';
import { useNotification } from '../context/NotificationContext';
import { formatDateDDMMYYYY, todayStr, firstOfMonthStr, NEPAL_TIME_ZONE } from '../utils/formatDate';
import ConfirmModal from '../components/ConfirmModal';

const formatTime = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: NEPAL_TIME_ZONE });
};

// Shift times are plain wall-clock 'HH:MM[:SS]' strings (not instants), so
// they're formatted by hand — no timezone conversion involved.
const formatShiftTime = (value) => {
  if (!value) return '—';
  const [h, m] = value.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};
const toTimeInput = (value) => (value ? value.slice(0, 5) : '');

// A stored naive Nepal 'YYYY-MM-DD HH:MM:SS' as "31-10 5:30 PM".
const formatNaiveNepal = (naive) => {
  if (!naive) return '—';
  const [date, time] = naive.split(' ');
  const [, month, day] = date.split('-');
  return `${day}-${month} ${formatShiftTime(time)}`;
};

const formatAgo = (minutes) => {
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m ago`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h ago`;
};

// A re-fetch request's lifecycle. "Sent" with no reply doesn't mean it
// failed — some firmware re-sends the punches without ever confirming.
const COMMAND_STATUS = {
  pending: { label: 'Waiting for the device to check in', color: 'var(--text-muted)' },
  sent: { label: 'Sent to the device — no reply yet', color: '#b45309' },
  done: { label: 'Device accepted', color: 'var(--success-color)' },
  failed: { label: 'Device rejected', color: 'var(--danger-color)' }
};

// A punch instant as Nepal wall-clock 'HH:MM' (for the time input).
const nepalHHMM = (value) => new Intl.DateTimeFormat('en-GB', {
  timeZone: NEPAL_TIME_ZONE, hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
}).format(new Date(value));

// Minutes into the business day (which starts at 5:00 AM, same as the backend),
// so a 12:30 AM checkout sorts after a 4:00 PM check-in instead of before it.
const businessMinutes = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h * 60 + m - 5 * 60 + 24 * 60) % (24 * 60);
};

const formatHours = (hours) => {
  if (!hours) return '—';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${m}m`;
};

const formatMinutes = (mins) => {
  if (!mins) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const statusBadge = (status, excusedHours) => {
  if (status === 'leave') return { label: 'Leave', bg: 'rgba(37, 99, 235, 0.1)', color: 'var(--primary-color)' };
  if (status === 'absent') return { label: 'Absent', bg: 'rgba(239, 68, 68, 0.12)', color: 'var(--danger-color)' };
  if (status === 'missing_punch') return { label: 'Missing punch', bg: 'rgba(249, 115, 22, 0.14)', color: '#c2410c' };
  if (status === 'excused_late') {
    const hoursLabel = excusedHours ? ` (${Number(excusedHours)}h)` : '';
    return { label: `Excused${hoursLabel}`, bg: 'rgba(234, 179, 8, 0.15)', color: '#b45309' };
  }
  return null;
};

const Attendance = () => {
  const { notifySuccess, notifyError, showLoading, hideLoading } = useNotification();
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'leave' | 'excused_late'
  const [range, setRange] = useState({ from: firstOfMonthStr(), to: todayStr() });
  const [expandedKey, setExpandedKey] = useState(null);
  const [leaveModal, setLeaveModal] = useState(null); // { employeeId, date, type, note }
  const [shiftModal, setShiftModal] = useState(null); // { employeeId, date, shiftStart, shiftEnd, overrideId }
  const [punchModal, setPunchModal] = useState(null); // { punchId, employeeId, date, time, note, locked }
  const [rangeRecalcEmployeeId, setRangeRecalcEmployeeId] = useState('');
  const [deviceStatus, setDeviceStatus] = useState(null); // { offlineAfterMinutes, devices, commands }
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, confirmLabel, onConfirm }

  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (range.from) params.set('from', range.from);
      if (range.to) params.set('to', range.to);
      const response = await apiFetch(`/api/attendance?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setRecords(data);
      }
    } catch (e) {
      console.error('Error fetching attendance');
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  const fetchEmployees = async () => {
    try {
      const response = await apiFetch('/api/employees');
      if (response.ok) {
        setEmployees(await response.json());
      }
    } catch (e) {
      console.error('Error fetching employees');
    }
  };

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  useEffect(() => {
    fetchEmployees();
  }, []);

  // Fingerprint device: whether it's checking in, and what happened to the
  // most recent "re-send missed punches" requests. Refreshed every 30s while
  // the page is open — the device itself checks in about that often.
  const fetchDeviceStatus = useCallback(async () => {
    try {
      const response = await apiFetch('/api/attendance/device-status');
      if (response.ok) setDeviceStatus(await response.json());
    } catch (e) {
      console.error('Error fetching device status');
    }
  }, []);

  useEffect(() => {
    fetchDeviceStatus();
    const interval = setInterval(fetchDeviceStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchDeviceStatus]);

  const requestDeviceRefetch = async () => {
    if (!range.from || !range.to) return;
    showLoading('Requesting punches from the device…');
    try {
      const response = await apiFetch('/api/attendance/device-refetch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from: range.from, to: range.to })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        fetchDeviceStatus();
        notifySuccess('Requested — if the device is online it picks this up within about a minute, then reload the page');
      } else {
        notifyError(data.error || 'Failed to request punches from the device');
      }
    } catch (e) {
      notifyError('Failed to request punches from the device');
    } finally {
      hideLoading();
    }
  };

  const filteredRecords = records.filter(r => {
    const matchesSearch = r.employeeName.toLowerCase().includes(searchQuery.trim().toLowerCase());
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const toggleExpanded = (key) => setExpandedKey(prev => (prev === key ? null : key));

  const openLeaveModal = (prefill) => setLeaveModal({
    employeeId: prefill?.employeeId ? String(prefill.employeeId) : '',
    date: prefill?.date || todayStr(),
    type: 'leave',
    excusedHours: '2',
    note: ''
  });
  const closeLeaveModal = () => setLeaveModal(null);

  // A day's shift defaults to the employee's own (Employees page) unless an
  // override already exists for that day, in which case that's what's shown.
  const defaultShiftFor = (employeeId) => {
    const emp = employees.find(e => String(e.id) === String(employeeId));
    return { shiftStart: toTimeInput(emp?.shift_start) || '09:00', shiftEnd: toTimeInput(emp?.shift_end) || '18:00' };
  };

  const openShiftModal = (prefill) => {
    const employeeId = prefill?.employeeId ? String(prefill.employeeId) : '';
    const override = prefill?.shiftOverride;
    setShiftModal({
      employeeId,
      date: prefill?.date || todayStr(),
      overrideId: override?.id || null,
      ...(override
        ? { shiftStart: toTimeInput(override.start), shiftEnd: toTimeInput(override.end) }
        : defaultShiftFor(employeeId))
    });
  };
  const closeShiftModal = () => setShiftModal(null);

  const submitShift = async (e) => {
    e.preventDefault();
    if (!shiftModal.employeeId) return;
    showLoading('Saving shift…');
    try {
      const response = await apiFetch('/api/attendance/shift-override', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employeeId: Number(shiftModal.employeeId),
          date: shiftModal.date,
          shiftStart: shiftModal.shiftStart,
          shiftEnd: shiftModal.shiftEnd
        })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        closeShiftModal();
        fetchAttendance();
        notifySuccess(data.recalculated
          ? 'Shift saved — that day has been recalculated'
          : 'Shift saved — it will be used when that day is calculated once it has finished');
      } else {
        notifyError(data.error || 'Failed to save shift');
      }
    } catch (e) {
      notifyError('Failed to save shift');
    } finally {
      hideLoading();
    }
  };

  // The shift that applies to a row's day — its override if one is set,
  // otherwise the employee's default.
  const rowShift = (r) => (r.shiftOverride
    ? { shiftStart: toTimeInput(r.shiftOverride.start), shiftEnd: toTimeInput(r.shiftOverride.end) }
    : defaultShiftFor(r.employeeId));

  // With exactly one punch recorded, guess which side is missing: a lone punch
  // nearer the shift end is probably the checkout (so suggest the shift start
  // as the missing check-in), otherwise suggest the shift end as the missing
  // checkout. Only a suggestion — the admin always confirms the time.
  const suggestMissingTime = (r) => {
    const { shiftStart, shiftEnd } = rowShift(r);
    if (!r.punches || r.punches.length !== 1) return shiftStart;
    const punch = businessMinutes(nepalHHMM(r.punches[0].time));
    const nearerEnd = Math.abs(punch - businessMinutes(shiftEnd)) < Math.abs(punch - businessMinutes(shiftStart));
    return nearerEnd ? shiftStart : shiftEnd;
  };

  const openAddPunch = (r) => setPunchModal({
    punchId: null,
    employeeId: r ? String(r.employeeId) : '',
    date: r ? r.date : todayStr(),
    time: r ? suggestMissingTime(r) : '',
    note: '',
    locked: !!r
  });

  const openEditPunch = (r, p) => setPunchModal({
    punchId: p.id,
    employeeId: String(r.employeeId),
    date: r.date,
    time: nepalHHMM(p.time),
    note: p.note || '',
    locked: true
  });
  const closePunchModal = () => setPunchModal(null);

  const submitPunch = async (e) => {
    e.preventDefault();
    if (!punchModal.employeeId || !punchModal.time || !punchModal.note.trim()) return;
    const isEdit = !!punchModal.punchId;
    showLoading(isEdit ? 'Updating punch…' : 'Adding punch…');
    try {
      const response = await apiFetch(isEdit ? `/api/attendance/punch/${punchModal.punchId}` : '/api/attendance/punch', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isEdit
          ? { time: punchModal.time, note: punchModal.note }
          : { employeeId: Number(punchModal.employeeId), date: punchModal.date, time: punchModal.time, note: punchModal.note })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        closePunchModal();
        fetchAttendance();
        notifySuccess(data.recalculated
          ? `Punch ${isEdit ? 'updated' : 'added'} — that day has been recalculated`
          : `Punch ${isEdit ? 'updated' : 'added'} — it will be used when the day is calculated once it has finished`);
      } else {
        notifyError(data.error || `Failed to ${isEdit ? 'update' : 'add'} punch`);
      }
    } catch (e) {
      notifyError(`Failed to ${isEdit ? 'update' : 'add'} punch`);
    } finally {
      hideLoading();
    }
  };

  const requestDeletePunch = (r, p) => {
    setConfirmModal({
      title: 'Remove This Punch?',
      message: `Remove the ${formatTime(p.time)} punch that was added by admin for ${r.employeeName} on ${formatDateDDMMYYYY(r.date)}? The day will be recalculated without it.`,
      confirmLabel: 'Remove Punch',
      danger: true,
      onConfirm: async () => {
        setConfirmModal(null);
        showLoading('Removing punch…');
        try {
          const response = await apiFetch(`/api/attendance/punch/${p.id}`, { method: 'DELETE' });
          const data = await response.json().catch(() => ({}));
          if (response.ok) {
            fetchAttendance();
            notifySuccess('Punch removed');
          } else {
            notifyError(data.error || 'Failed to remove punch');
          }
        } catch (e) {
          notifyError('Failed to remove punch');
        } finally {
          hideLoading();
        }
      }
    });
  };

  const resetShift = async () => {
    showLoading('Resetting shift…');
    try {
      const response = await apiFetch(`/api/attendance/shift-override/${shiftModal.overrideId}`, { method: 'DELETE' });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        closeShiftModal();
        fetchAttendance();
        notifySuccess(data.recalculated ? 'Back to the default shift — that day has been recalculated' : 'Back to the default shift');
      } else {
        notifyError(data.error || 'Failed to reset shift');
      }
    } catch (e) {
      notifyError('Failed to reset shift');
    } finally {
      hideLoading();
    }
  };

  const submitLeave = async (e) => {
    e.preventDefault();
    if (!leaveModal.employeeId) return;
    if (leaveModal.type === 'excused_late' && !leaveModal.excusedHours) {
      notifyError('Enter how many hours of lateness to excuse');
      return;
    }
    showLoading('Saving…');
    try {
      const response = await apiFetch('/api/attendance/leave', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employeeId: Number(leaveModal.employeeId),
          date: leaveModal.date,
          type: leaveModal.type,
          excusedHours: leaveModal.type === 'excused_late' ? Number(leaveModal.excusedHours) : undefined,
          note: leaveModal.note
        })
      });
      if (response.ok) {
        closeLeaveModal();
        fetchAttendance();
        notifySuccess('Leave recorded');
      } else {
        const data = await response.json().catch(() => ({}));
        notifyError(data.error || 'Failed to record leave');
      }
    } catch (e) {
      notifyError('Failed to record leave');
    } finally {
      hideLoading();
    }
  };

  // Late/break/overtime figures for today are computed automatically once
  // it becomes "yesterday" (so an in-progress shift never gets a premature
  // deduction). Once a shift has actually finished, this forces that day's
  // numbers to appear right away instead of waiting until tomorrow.
  const handleRecalculate = async (employeeId, date) => {
    showLoading('Recalculating…');
    try {
      const response = await apiFetch('/api/attendance/recalculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeId, date })
      });
      if (response.ok) {
        fetchAttendance();
        notifySuccess('Recalculated');
      } else {
        const data = await response.json().catch(() => ({}));
        notifyError(data.error || 'Failed to recalculate');
      }
    } catch (e) {
      notifyError('Failed to recalculate');
    } finally {
      hideLoading();
    }
  };

  // Repairs a stretch that was computed against wrong shift hours — the
  // automatic pass only walks forward, so it never revisits days already
  // computed. Re-runs every recorded day in the selected date range for one
  // employee against their CURRENT shift/grace settings.
  const requestRecalculateRange = () => {
    const allEmployees = rangeRecalcEmployeeId === 'all';
    const emp = employees.find(e => String(e.id) === String(rangeRecalcEmployeeId));
    if ((!allEmployees && !emp) || !range.from || !range.to) return;
    const who = allEmployees ? 'every employee' : emp.name;
    setConfirmModal({
      title: 'Recalculate This Range?',
      message: `Re-run ${allEmployees ? "every employee's" : `${emp.name}'s`} recorded days from ${formatDateDDMMYYYY(range.from)} to ${formatDateDDMMYYYY(range.to)} against current shift hours, shift overrides, added punches and grace rules. The salary ledger is adjusted by the difference. Today is skipped while shifts are still running. Days that were already paid out in an earlier cycle will shift the current cycle's totals instead.`,
      confirmLabel: 'Recalculate',
      onConfirm: async () => {
        setConfirmModal(null);
        showLoading('Recalculating range…');
        try {
          const response = await apiFetch('/api/attendance/recalculate-range', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ employeeId: allEmployees ? undefined : emp.id, from: range.from, to: range.to })
          });
          const data = await response.json().catch(() => ({}));
          if (response.ok) {
            fetchAttendance();
            notifySuccess(`Recalculated ${data.daysRecalculated} ${data.daysRecalculated === 1 ? 'day' : 'days'} for ${who}`);
          } else {
            notifyError(data.error || 'Failed to recalculate range');
          }
        } catch (e) {
          notifyError('Failed to recalculate range');
        } finally {
          hideLoading();
        }
      }
    });
  };

  return (
    <div className="attendance-page">
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700' }}>Attendance</h1>
          <p style={{ color: 'var(--text-muted)' }}>Fingerprint check-in/check-out records by day</p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <button
            className="btn"
            style={{ background: 'var(--surface-color)', border: '1px solid var(--border-color)' }}
            onClick={() => openAddPunch(null)}
          >
            <Plus size={18} style={{ marginRight: '8px' }} />
            Add Punch
          </button>
          <button
            className="btn"
            style={{ background: 'var(--surface-color)', border: '1px solid var(--border-color)' }}
            onClick={() => openShiftModal(null)}
          >
            <Clock size={18} style={{ marginRight: '8px' }} />
            Set Shift for a Day
          </button>
          <button className="btn btn-primary" onClick={() => openLeaveModal(null)}>
            <CalendarOff size={18} style={{ marginRight: '8px' }} />
            Mark Leave / Excuse
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <div style={{ position: 'relative', maxWidth: '360px', flex: '1 1 260px' }}>
          <Search size={18} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="text"
            className="input-field"
            style={{ paddingLeft: '42px' }}
            placeholder="Search employee by name…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          {[
            { key: 'all', label: 'All' },
            { key: 'leave', label: 'On Leave' },
            { key: 'excused_late', label: 'Excused' },
            { key: 'absent', label: 'Absent' },
            { key: 'missing_punch', label: 'Missing Punch' }
          ].map(f => (
            <button
              key={f.key}
              className="btn"
              style={{
                background: statusFilter === f.key ? 'var(--primary-color)' : 'var(--surface-color)',
                color: statusFilter === f.key ? 'white' : 'var(--text-main)',
                border: '1px solid var(--border-color)',
                padding: '10px 20px',
                fontSize: '13px'
              }}
              onClick={() => setStatusFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', padding: '14px 20px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-muted)', fontWeight: '500' }}>
          <CalendarRange size={16} />
          Date Range
        </div>
        <input
          type="date"
          className="input-field"
          style={{ padding: '8px 10px', fontSize: '13px', width: 'auto' }}
          max={range.to || todayStr()}
          value={range.from}
          onChange={e => setRange(prev => ({ ...prev, from: e.target.value }))}
        />
        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>to</span>
        <input
          type="date"
          className="input-field"
          style={{ padding: '8px 10px', fontSize: '13px', width: 'auto' }}
          min={range.from}
          max={todayStr()}
          value={range.to}
          onChange={e => setRange(prev => ({ ...prev, to: e.target.value }))}
        />
        <button
          onClick={() => setRange({ from: todayStr(), to: todayStr() })}
          style={{ background: 'none', border: 'none', color: 'var(--primary-color)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}
        >
          Today
        </button>
        <button
          onClick={() => setRange({ from: firstOfMonthStr(), to: todayStr() })}
          style={{ background: 'none', border: 'none', color: 'var(--primary-color)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}
        >
          <X size={14} /> Reset to this month
        </button>
      </div>

      <div className="card" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', padding: '14px 20px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-muted)', fontWeight: '500' }}>
          <RefreshCw size={16} />
          Recalculate range
        </div>
        <select
          className="input-field"
          style={{ padding: '8px 10px', fontSize: '13px', width: 'auto' }}
          value={rangeRecalcEmployeeId}
          onChange={e => setRangeRecalcEmployeeId(e.target.value)}
        >
          <option value="">Select employee…</option>
          <option value="all">All employees</option>
          {employees.map(emp => (
            <option key={emp.id} value={emp.id}>{emp.name}</option>
          ))}
        </select>
        <button
          className="btn"
          style={{ padding: '8px 14px', fontSize: '13px', background: 'var(--bg-color)' }}
          disabled={!rangeRecalcEmployeeId || !range.from || !range.to}
          onClick={requestRecalculateRange}
        >
          Recalculate
        </button>
        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
          Re-runs the days in the date range above against their current shift hours — use after fixing wrong shift times.
        </span>
      </div>

      {deviceStatus && (
        <div className="card" style={{ padding: '14px 20px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-muted)', fontWeight: '500' }}>
              <Fingerprint size={16} />
              Fingerprint device
            </div>
            {deviceStatus.devices.length === 0 ? (
              <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>hasn't checked in since this feature was installed</span>
            ) : deviceStatus.devices.map(d => {
              const online = d.minutesSinceSeen < deviceStatus.offlineAfterMinutes;
              return (
                <span key={d.serialNumber} style={{ fontSize: '13px', fontWeight: '600', color: online ? 'var(--success-color)' : 'var(--danger-color)' }}>
                  ● {online ? 'Online' : 'Offline'}
                  <span style={{ fontWeight: '400', color: 'var(--text-muted)' }}>
                    {' '}— last check-in {formatAgo(d.minutesSinceSeen)} ({formatNaiveNepal(d.lastSeenAt)})
                  </span>
                </span>
              );
            })}
            <button
              className="btn"
              style={{ padding: '8px 14px', fontSize: '13px', background: 'var(--bg-color)', marginLeft: 'auto' }}
              disabled={!range.from || !range.to}
              onClick={requestDeviceRefetch}
              title="Ask the device to re-send every punch it recorded in the date range above. Punches already in the app are skipped."
            >
              <RefreshCw size={14} style={{ marginRight: '6px' }} />
              Fetch missed punches from device
            </button>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '8px 0 0' }}>
            The device keeps punches it couldn't send while offline but doesn't re-send them by itself. When it comes back
            after a break this is requested automatically; use the button to recover any date range above.
          </p>
          {deviceStatus.commands.length > 0 && (
            <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {deviceStatus.commands.slice(0, 3).map(c => (
                <div key={c.id} style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  {formatNaiveNepal(c.created_at)} · {c.reason} ·{' '}
                  <strong style={{ color: COMMAND_STATUS[c.status].color }}>
                    {COMMAND_STATUS[c.status].label}{c.status === 'failed' && c.return_code ? ` (code ${c.return_code})` : ''}
                  </strong>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
        <div className="table-scroll">
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '780px' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--bg-color)', borderBottom: '1px solid var(--border-color)' }}>
                <th style={{ padding: '16px' }}>Employee</th>
                <th style={{ padding: '16px' }}>Date</th>
                <th style={{ padding: '16px' }}>Status</th>
                <th style={{ padding: '16px' }}>First In</th>
                <th style={{ padding: '16px' }}>Last Out</th>
                <th style={{ padding: '16px' }}>Total Hours</th>
                <th style={{ padding: '16px' }}>Punches</th>
                <th style={{ padding: '16px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map(r => {
                const key = `${r.employeeId ?? 'u'}-${r.date}`;
                const isUnmapped = r.employeeId === null;
                const isExpanded = expandedKey === key;
                const badge = statusBadge(r.status, r.excusedHours);
                const lateLabel = formatMinutes(r.lateMinutes);
                const breakLabel = formatMinutes(r.breakMinutes);
                const earlyLabel = formatMinutes(r.earlyDepartureMinutes);
                const otLabel = formatMinutes(r.overtimeMinutes);
                const hasCalc = lateLabel || breakLabel || earlyLabel || otLabel;
                return (
                  <React.Fragment key={key}>
                    <tr style={{ borderBottom: isExpanded ? 'none' : '1px solid var(--border-color)' }}>
                      <td style={{ padding: '16px', fontWeight: '500' }}>
                        {isUnmapped && <Fingerprint size={14} style={{ marginRight: '6px', color: 'var(--warning-color)', verticalAlign: 'middle' }} />}
                        {r.employeeName}
                      </td>
                      <td style={{ padding: '16px', color: 'var(--text-muted)' }}>
                        {formatDateDDMMYYYY(r.date)}
                        {r.shiftOverride && (
                          <div style={{ fontSize: '11px', color: 'var(--primary-color)', marginTop: '2px', whiteSpace: 'nowrap' }}>
                            Shift {formatShiftTime(r.shiftOverride.start)} – {formatShiftTime(r.shiftOverride.end)}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '16px' }}>
                        {badge ? (
                          <span style={{ fontSize: '12px', fontWeight: '600', padding: '3px 10px', borderRadius: '999px', background: badge.bg, color: badge.color }}>
                            {badge.label}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '16px' }}>{formatTime(r.firstIn)}</td>
                      <td style={{ padding: '16px' }}>{formatTime(r.lastOut)}</td>
                      <td style={{ padding: '16px' }}>{formatHours(r.totalHours)}</td>
                      <td style={{ padding: '16px' }}>
                        {r.punchCount > 0 ? (
                          <button
                            className="btn"
                            style={{ padding: '6px 12px', fontSize: '12px', background: 'var(--bg-color)' }}
                            onClick={() => toggleExpanded(key)}
                          >
                            {r.punchCount} {r.punchCount === 1 ? 'punch' : 'punches'}
                            {isExpanded ? <ChevronUp size={14} style={{ marginLeft: '6px' }} /> : <ChevronDown size={14} style={{ marginLeft: '6px' }} />}
                          </button>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '16px', textAlign: 'right', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        {r.employeeId && (
                          <>
                            <button
                              className="icon-btn"
                              style={{ color: 'var(--text-muted)' }}
                              title="Recalculate this day now (skips the automatic wait-until-tomorrow)"
                              onClick={() => handleRecalculate(r.employeeId, r.date)}
                            >
                              <RefreshCw size={16} />
                            </button>
                            <button
                              className="icon-btn"
                              style={{ color: r.status === 'missing_punch' ? '#c2410c' : 'var(--text-muted)' }}
                              title={r.status === 'missing_punch' ? 'Only one punch recorded — add the missing punch' : 'Add a punch the employee forgot'}
                              onClick={() => openAddPunch(r)}
                            >
                              <Plus size={16} />
                            </button>
                            <button
                              className="icon-btn"
                              style={{ color: r.shiftOverride ? 'var(--primary-color)' : 'var(--text-muted)' }}
                              title={r.shiftOverride ? "This day uses a custom shift — click to change or reset it" : "Set a different shift for this day"}
                              onClick={() => openShiftModal({ employeeId: r.employeeId, date: r.date, shiftOverride: r.shiftOverride })}
                            >
                              <Clock size={16} />
                            </button>
                            <button
                              className="icon-btn"
                              style={{ color: 'var(--primary-color)' }}
                              title="Mark leave / excuse for this day"
                              onClick={() => openLeaveModal({ employeeId: r.employeeId, date: r.date })}
                            >
                              <CalendarOff size={16} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td colSpan="8" style={{ padding: '0 16px 16px 16px', background: 'var(--bg-color)' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', paddingTop: '4px' }}>
                            {r.punches.map(p => (p.source === 'manual' ? (
                              <span
                                key={p.id}
                                title={p.note ? `Reason: ${p.note}` : undefined}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', padding: '4px 6px 4px 10px', borderRadius: '6px', background: 'rgba(249, 115, 22, 0.1)', border: '1px dashed #c2410c', color: '#9a3412' }}
                              >
                                {formatTime(p.time)} · Added by admin
                                <button className="icon-btn" style={{ color: '#9a3412', padding: '2px' }} title="Edit this punch" onClick={() => openEditPunch(r, p)}>
                                  <Pencil size={12} />
                                </button>
                                <button className="icon-btn" style={{ color: 'var(--danger-color)', padding: '2px' }} title="Remove this punch" onClick={() => requestDeletePunch(r, p)}>
                                  <Trash2 size={12} />
                                </button>
                              </span>
                            ) : (
                              <span
                                key={p.id}
                                style={{ fontSize: '12px', padding: '6px 10px', borderRadius: '6px', background: 'var(--surface-color)', border: '1px solid var(--border-color)' }}
                              >
                                {formatTime(p.time)} · {p.verifyMethod || 'unknown'}
                              </span>
                            )))}
                          </div>
                          {r.punches.filter(p => p.source === 'manual' && p.note).map(p => (
                            <div key={`note-${p.id}`} style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>
                              {formatTime(p.time)} added by admin — <em>{p.note}</em>
                            </div>
                          ))}
                          {r.status === 'missing_punch' && (
                            <div style={{ fontSize: '12px', color: '#c2410c', marginTop: '10px' }}>
                              Only one punch recorded, so nothing is deducted for this day yet.{' '}
                              <button
                                className="btn"
                                style={{ padding: '2px 8px', fontSize: '12px', background: 'none', color: '#c2410c', textDecoration: 'underline' }}
                                onClick={() => openAddPunch(r)}
                              >
                                Add the missing punch
                              </button>
                            </div>
                          )}
                          {hasCalc && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '10px', fontSize: '12px', color: 'var(--text-muted)' }}>
                              {lateLabel && <span>Late by <strong style={{ color: 'var(--danger-color)' }}>{lateLabel}</strong> beyond grace</span>}
                              {breakLabel && <span>Break over by <strong style={{ color: 'var(--danger-color)' }}>{breakLabel}</strong></span>}
                              {earlyLabel && <span>Short of shift hours by <strong style={{ color: 'var(--danger-color)' }}>{earlyLabel}</strong></span>}
                              {otLabel && (
                                <span>
                                  Overtime <strong style={{ color: 'var(--success-color)' }}>{otLabel}</strong>
                                  {r.overtimeAmount > 0 && <> (Rs{r.overtimeAmount.toFixed(2)})</>}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {!loading && filteredRecords.length === 0 && (
                <tr>
                  <td colSpan="8" style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    {records.length === 0 ? 'No attendance records found for this date range.' : `No employee found matching "${searchQuery}".`}
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan="8" style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {leaveModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={closeLeaveModal}
        >
          <div className="card" style={{ width: '380px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <form onSubmit={submitLeave}>
              <h3 style={{ marginBottom: '16px' }}>Mark Leave / Excuse</h3>

              <div style={{ marginBottom: '16px' }}>
                <label className="label">Employee</label>
                <select
                  className="input-field"
                  required
                  value={leaveModal.employeeId}
                  onChange={e => setLeaveModal(prev => ({ ...prev, employeeId: e.target.value }))}
                >
                  <option value="" disabled>Select employee…</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label className="label">Date</label>
                <input
                  type="date"
                  className="input-field"
                  required
                  max={todayStr()}
                  value={leaveModal.date}
                  onChange={e => setLeaveModal(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label className="label">Type</label>
                <select
                  className="input-field"
                  value={leaveModal.type}
                  onChange={e => setLeaveModal(prev => ({ ...prev, type: e.target.value }))}
                >
                  <option value="leave">Leave (full day paid, no deduction)</option>
                  <option value="excused_late">Excused Hours (covers late arrival, break, or leaving early)</option>
                </select>
              </div>

              {leaveModal.type === 'excused_late' && (
                <div style={{ marginBottom: '16px' }}>
                  <label className="label">Hours Excused</label>
                  <input
                    type="number"
                    className="input-field"
                    min="0.5"
                    step="0.5"
                    required
                    placeholder="e.g. 2"
                    value={leaveModal.excusedHours}
                    onChange={e => setLeaveModal(prev => ({ ...prev, excusedHours: e.target.value }))}
                  />
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                    Applies to whatever ran over that day — late arrival, a long break, or leaving early. Time up to this many hours is not deducted; anything beyond it still is.
                  </p>
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <label className="label">Note (optional)</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. informed in advance, family event"
                  value={leaveModal.note}
                  onChange={e => setLeaveModal(prev => ({ ...prev, note: e.target.value }))}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button type="button" className="btn" onClick={closeLeaveModal}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {shiftModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={closeShiftModal}
        >
          <div className="card" style={{ width: '380px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <form onSubmit={submitShift}>
              <h3 style={{ marginBottom: '16px' }}>Set Shift for a Day</h3>

              <div style={{ marginBottom: '16px' }}>
                <label className="label">Employee</label>
                <select
                  className="input-field"
                  required
                  value={shiftModal.employeeId}
                  onChange={e => setShiftModal(prev => ({
                    ...prev,
                    employeeId: e.target.value,
                    overrideId: null,
                    ...defaultShiftFor(e.target.value)
                  }))}
                  disabled={!!shiftModal.overrideId}
                >
                  <option value="" disabled>Select employee…</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label className="label">Date</label>
                <input
                  type="date"
                  className="input-field"
                  required
                  value={shiftModal.date}
                  disabled={!!shiftModal.overrideId}
                  onChange={e => setShiftModal(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
                <div style={{ flex: 1 }}>
                  <label className="label">Shift Start</label>
                  <input
                    type="time"
                    className="input-field"
                    required
                    value={shiftModal.shiftStart}
                    onChange={e => setShiftModal(prev => ({ ...prev, shiftStart: e.target.value }))}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="label">Shift End</label>
                  <input
                    type="time"
                    className="input-field"
                    required
                    value={shiftModal.shiftEnd}
                    onChange={e => setShiftModal(prev => ({ ...prev, shiftEnd: e.target.value }))}
                  />
                </div>
              </div>
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '20px' }}>
                Applies to this one day only — the employee's default shift on the Employees page isn't changed.
                An end time earlier than the start (e.g. 4:00 PM → 12:00 AM) means the shift runs past midnight.
                If the day has already happened it's recalculated right away.
              </p>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                {shiftModal.overrideId ? (
                  <button type="button" className="btn" style={{ color: 'var(--danger-color)' }} onClick={resetShift}>
                    Reset to default
                  </button>
                ) : <span />}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button type="button" className="btn" onClick={closeShiftModal}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Save</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {punchModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={closePunchModal}
        >
          <div className="card" style={{ width: '380px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <form onSubmit={submitPunch}>
              <h3 style={{ marginBottom: '16px' }}>{punchModal.punchId ? 'Edit Added Punch' : 'Add Missing Punch'}</h3>

              <div style={{ marginBottom: '16px' }}>
                <label className="label">Employee</label>
                <select
                  className="input-field"
                  required
                  value={punchModal.employeeId}
                  disabled={punchModal.locked}
                  onChange={e => setPunchModal(prev => ({ ...prev, employeeId: e.target.value }))}
                >
                  <option value="" disabled>Select employee…</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
                <div style={{ flex: 1 }}>
                  <label className="label">Shift Day</label>
                  <input
                    type="date"
                    className="input-field"
                    required
                    max={todayStr()}
                    value={punchModal.date}
                    disabled={punchModal.locked}
                    onChange={e => setPunchModal(prev => ({ ...prev, date: e.target.value }))}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="label">Time</label>
                  <input
                    type="time"
                    className="input-field"
                    required
                    value={punchModal.time}
                    onChange={e => setPunchModal(prev => ({ ...prev, time: e.target.value }))}
                  />
                </div>
              </div>
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>
                A time before 5:00 AM counts as the end of this day's shift (after midnight).
                {!punchModal.punchId && punchModal.locked && ' The time is pre-filled with a guess from the shift — change it to the real time.'}
              </p>

              <div style={{ marginBottom: '20px' }}>
                <label className="label">Reason (required)</label>
                <input
                  type="text"
                  className="input-field"
                  required
                  maxLength={255}
                  placeholder="e.g. forgot to punch out, confirmed by manager"
                  value={punchModal.note}
                  onChange={e => setPunchModal(prev => ({ ...prev, note: e.target.value }))}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button type="button" className="btn" onClick={closePunchModal}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmLabel={confirmModal?.confirmLabel}
        danger={confirmModal?.danger}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default Attendance;
